import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/dashboard/Dashboard";
import PropertyList from "./pages/properties/PropertyList";
import PropertyDetail from "./pages/properties/PropertyDetail";
import Appointments from "./pages/appointments/Appointments";
import Bookings from "./pages/bookings/Bookings";
import CreateBooking from "./pages/bookings/CreateBooking";
import Wallet from "./pages/wallet/Wallet";
import Profile from "./pages/profile/Profile";
import Referrals from "./pages/referrals/Referrals";
import Claims from "./pages/claims/Claims";
import AgentCommissionClaims from "./pages/claims/AgentCommissionClaims";
import PaymentSlips from "./pages/claims/PaymentSlips";

import PropertyListPublic from "./pages/properties/PropertyListPublic";
import ReferralLanding from "./pages/referral/ReferralLanding";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProperties from "./pages/admin/AdminProperties";
import UnitAvailability from "./pages/admin/UnitAvailability";
import AgentManagement from "./pages/admin/AgentManagement";
import AgentDetails from "./pages/admin/AgentDetails";
import AdminKYC from "./pages/admin/AdminKYC";
import AdminClaims from "./pages/admin/AdminClaims";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminConfig from "./pages/admin/AdminConfig";
import AdminPdfTemplates from "./pages/admin/AdminPdfTemplates";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import CommissionAnalytics from "./pages/admin/CommissionAnalytics";
import AdminSamRumahGroup from "./pages/admin/AdminSamRumahGroup";
import AdminBookingTemplates from "./pages/admin/AdminBookingTemplates";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminPushNotifications from "./pages/admin/AdminPushNotifications";
import AdminDeveloperTemplates from "./pages/admin/AdminDeveloperTemplates";
import AdminCommissionClaims from "./pages/admin/AdminCommissionClaims";
import AdminRankConfig from "./pages/admin/AdminRankConfig";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminCustomerManagement from "./pages/admin/AdminCustomerManagement";
import AdminCustomerDetails from "./pages/admin/AdminCustomerDetails";
import PaymentVerification from "./pages/admin/PaymentVerification";

import { EntityProvider } from "./context/EntityContext";

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <EntityProvider>
        <div className="App">
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Public Property Routes */}
            <Route path="/properties" element={<PropertyListPublic />} />
            <Route path="/ref/:code" element={<ReferralLanding />} />
            
            {/* Customer/Agent Routes */}
            <Route element={<DashboardLayout allowedRoles={['customer', 'agent']} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/agent-properties" element={<PropertyList />} />
              <Route path="/properties/:id" element={<PropertyDetail />} />
              <Route path="/my-appointments" element={<Appointments />} />
              <Route path="/appointments" element={<Appointments />} />
              <Route path="/my-bookings" element={<Bookings />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/bookings/create" element={<CreateBooking />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/claims" element={<Claims />} />
              <Route path="/commission-claims" element={<AgentCommissionClaims />} />
              <Route path="/payment-slips" element={<PaymentSlips />} />
              <Route path="/my-team" element={<Referrals />} />
              <Route path="/performance" element={<Dashboard />} />
            </Route>

            {/* Admin/PIC/Account Routes */}
            <Route element={<DashboardLayout allowedRoles={['admin', 'pic', 'account']} />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/commission-analytics" element={<CommissionAnalytics />} />
              <Route path="/admin/properties" element={<AdminProperties />} />
              <Route path="/admin/units" element={<UnitAvailability />} />
              <Route path="/admin/agents" element={<AgentManagement />} />
              <Route path="/admin/agents/:id" element={<AgentDetails />} />
              <Route path="/admin/customers" element={<AdminCustomerManagement />} />
              <Route path="/admin/customers/:id" element={<AdminCustomerDetails />} />
              <Route path="/admin/kyc" element={<AdminKYC />} />
              <Route path="/admin/appointments" element={<Appointments />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/payment-verification" element={<PaymentVerification />} />
              <Route path="/admin/claims" element={<AdminClaims />} />
              <Route path="/admin/commission-claims" element={<AdminCommissionClaims />} />
              <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
              <Route path="/admin/config" element={<AdminConfig />} />
              <Route path="/admin/pdf-templates" element={<AdminPdfTemplates />} />
              <Route path="/admin/booking-templates" element={<AdminBookingTemplates />} />
              <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
              <Route path="/admin/push-notifications" element={<AdminPushNotifications />} />
              <Route path="/admin/developer-templates" element={<AdminDeveloperTemplates />} />
              <Route path="/admin/sam-rumah-group" element={<AdminSamRumahGroup />} />
              <Route path="/admin/rank-config" element={<AdminRankConfig />} />
              <Route path="/admin/promotions" element={<AdminPromotions />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </div>
        </EntityProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
